<template>
  <div>
    <!-- Header -->
    <b-card no-body>
      <b-card-header>
        <b-card-title>المعاملات</b-card-title>
        <b-button
          variant="primary"
          :to="{ name: 'service-types-create' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">اضافة معاملة</span>
        </b-button>
      </b-card-header>

      <!-- Filters -->
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('common.search')"
              label-for="search"
            >
              <b-form-input
                id="search"
                v-model="filters.search"
                placeholder="البحث في المعاملات"
                @input="debouncedSearch"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('common.status')"
              label-for="status"
            >
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('serviceTypes.filters.sort_by')"
              label-for="sort-by"
            >
              <v-select
                v-model="filters.sort_by"
                :options="sortOptions"
                :reduce="option => option.value"
                label="label"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('serviceTypes.filters.gl_activity')"
              label-for="gl-activity"
            >
              <v-select
                v-model="filters.gl_activity"
                :options="activityFilterOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('serviceTypes.filters.all')"
                @input="onActivityFilter"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table -->
    <b-card
      no-body
      class="mt-2"
    >
      <b-table
        ref="refServiceTypesTable"
        :items="serviceTypes"
        :fields="tableColumns"
        :busy="loading"
        responsive
        show-empty
        :empty-text="$t('serviceTypes.messages.no_service_types_found')"
        class="position-relative"
      >
        <!-- Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <!-- Color -->
        <template #cell(color)="data">
          <div class="d-flex align-items-center">
            <div
              class="rounded-circle mr-50"
              :style="{
                width: '24px',
                height: '24px',
                backgroundColor: data.item.color || '#ccc'
              }"
            />
            <span>{{ data.item.color || 'N/A' }}</span>
          </div>
        </template>

        <!-- Icon -->
        <template #cell(icon)="data">
          <feather-icon
            v-if="data.item.icon"
            :icon="data.item.icon"
            size="20"
          />
          <span v-else>-</span>
        </template>

        <!-- Accounting activity -->
        <template #cell(gl_activity)="data">
          <gl-activity-chip
            :activity="data.item.effective_gl_activity"
            :inferred="data.item.gl_activity_inferred"
          />
        </template>

        <!-- Status -->
        <template #cell(is_active)="data">
          <b-badge
            :variant="data.item.is_active ? 'success' : 'danger'"
            pill
            class="cursor-pointer"
            @click="toggleStatus(data.item)"
          >
            {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
          </b-badge>
        </template>

        <!-- Services Count -->
        <template #cell(services_count)="data">
          <b-badge
            variant="light-primary"
            pill
          >
            {{ data.item.services_count || 0 }}
          </b-badge>
        </template>

        <!-- Attachment Lookups -->
        <template #cell(attachment_lookups)="data">
          <div
            v-if="getAttachmentLookupNames(data.item).length"
            class="d-flex flex-wrap"
          >
            <b-badge
              v-for="name in getAttachmentLookupNames(data.item)"
              :key="`${data.item.id}-${name}`"
              variant="light-info"
              class="mr-50 mb-50"
            >
              {{ name }}
            </b-badge>
          </div>
          <span v-else>-</span>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              variant="flat-info"
              size="sm"
              :to="{ name: 'service-types-edit', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              :disabled="!data.item.can_be_deleted"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-card-body class="d-flex justify-content-between align-items-center pt-0">
        <div>
          <span class="text-muted">{{ $t('common.showing') }} {{ meta.from || 0 }} {{ $t('common.to') }} {{ meta.to || 0 }} {{ $t('common.of') }} {{ meta.total || 0 }} {{ $t('common.entries') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="filters.per_page"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import { debounce } from '@/utils/helper'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { loadGlActivityOptions } from '@/api/services/serviceTypes'
import GlActivityChip from './GlActivityChip.vue'

export default {
  name: 'ServiceTypesList',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    vSelect,
    GlActivityChip,
  },
  data() {
    return {
      filters: {
        search: '',
        is_active: null,
        sort_by: 'order',
        sort_direction: 'asc',
        per_page: 15,
        gl_activity: this.$route.query.gl_activity || null,
      },
      currentPage: 1,
      tableColumns: [],
      statusOptions: [],
      sortOptions: [],
      activityFilterOptions: [],
    }
  },
  computed: {
    ...mapState('serviceTypes', ['serviceTypes', 'meta', 'loading']),
  },
  created() {
    this.initializeOptions()
    this.loadActivityFilter()
    this.fetchData()
    this.debouncedSearch = debounce(this.fetchData, 500)
  },
  methods: {
    ...mapActions('serviceTypes', [
      'fetchServiceTypes',
      'deleteServiceType',
      'toggleServiceTypeStatus',
    ]),
    initializeOptions() {
      this.tableColumns = [
        { key: 'order', label: this.$t('serviceTypes.fields.order'), sortable: true },
        { key: 'name', label: this.$t('serviceTypes.fields.name'), sortable: true },
        { key: 'name_ar', label: this.$t('serviceTypes.fields.name_ar'), sortable: true },
        { key: 'gl_activity', label: this.$t('serviceTypes.fields.gl_activity') },
        { key: 'color', label: this.$t('serviceTypes.fields.color') },
        { key: 'icon', label: this.$t('serviceTypes.fields.icon') },
        { key: 'is_active', label: this.$t('common.status') },
        { key: 'services_count', label: this.$t('serviceTypes.fields.services_count') },
        { key: 'attachment_lookups', label: this.$t('serviceTypes.fields.attachment_lookups') },
        { key: 'actions', label: this.$t('common.actions') },
      ]
      this.statusOptions = [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ]
      this.sortOptions = [
        { label: this.$t('serviceTypes.filters.order'), value: 'order' },
        { label: this.$t('serviceTypes.filters.name'), value: 'name' },
        { label: this.$t('serviceTypes.filters.created_date'), value: 'created_at' },
      ]
    },
    async loadActivityFilter() {
      try {
        const options = await loadGlActivityOptions()
        this.activityFilterOptions = [
          { label: this.$t('serviceTypes.filters.all'), value: null },
          ...options.map(option => ({
            label: option.label_ar || option.label,
            value: option.value,
          })),
        ]
      } catch (error) {
        this.activityFilterOptions = [{ label: this.$t('serviceTypes.filters.all'), value: null }]
      }
    },
    onActivityFilter() {
      const query = { ...this.$route.query }
      if (this.filters.gl_activity) query.gl_activity = this.filters.gl_activity
      else delete query.gl_activity
      this.$router.replace({ query }).catch(() => {})
      this.currentPage = 1
      this.fetchData()
    },
    async fetchData() {
      try {
        const params = {
          ...this.filters,
          page: this.currentPage,
        }
        if (!params.gl_activity) delete params.gl_activity
        await this.fetchServiceTypes(params)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('serviceTypes.messages.load_error'),
          },
        })
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    async toggleStatus(item) {
      this.$swal({
        title: this.$t('serviceTypes.messages.confirm_delete'),
        text: item.is_active ? this.$t('serviceTypes.messages.confirm_deactivate') : this.$t('serviceTypes.messages.confirm_activate'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.toggleServiceTypeStatus(item.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('serviceTypes.messages.status_update_success'),
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || this.$t('serviceTypes.messages.update_error'),
              },
            })
          }
        }
      })
    },
    confirmDelete(item) {
      if (!item.can_be_deleted) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('serviceTypes.messages.cannot_delete'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: this.$t('serviceTypes.messages.cannot_delete_text'),
          },
        })
        return
      }

      this.$swal({
        title: this.$t('serviceTypes.messages.confirm_delete'),
        text: this.$t('serviceTypes.messages.confirm_delete_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.deleteServiceType(item.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('serviceTypes.messages.delete_success'),
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || this.$t('serviceTypes.messages.delete_error'),
              },
            })
          }
        }
      })
    },
    lookupLabel(lookup, isArabic) {
      if (!lookup || typeof lookup !== 'object') return ''
      return isArabic
        ? (lookup.name_ar || lookup.name || lookup.label || lookup.key)
        : (lookup.name || lookup.name_ar || lookup.label || lookup.key)
    },
    departmentLabelFromLookup(lookup, isArabic) {
      if (!lookup || typeof lookup !== 'object') return ''
      return isArabic
        ? (lookup.attachment_department_name_ar || lookup.attachment_department_name)
        : (lookup.attachment_department_name || lookup.attachment_department_name_ar)
    },
    departmentLabelFromAssignment(assignment, isArabic) {
      if (!assignment || typeof assignment !== 'object') return ''
      return isArabic
        ? (assignment.attachment_department_name_ar || assignment.attachment_department_name)
        : (assignment.attachment_department_name || assignment.attachment_department_name_ar)
    },
    lookupMetaSuffix(lookup) {
      if (!lookup || typeof lookup !== 'object') return ''
      const extensions = Array.isArray(lookup.extensions) ? lookup.extensions.filter(Boolean) : []
      const extPart = extensions.length ? extensions.join(', ') : ''
      const multiple = lookup.is_multiple === true || lookup.is_multiple === 1 || lookup.is_multiple === 'true'
      const countPart = multiple
        ? this.$t('serviceTypes.fields.multiple_files')
        : this.$t('serviceTypes.fields.single_file')
      return [extPart, countPart].filter(Boolean).join(' · ')
    },
    getAttachmentLookupNames(item) {
      const isArabic = (this.$i18n?.locale || '').startsWith('ar')
      const assignments = Array.isArray(item?.attachment_lookup_assignments)
        ? item.attachment_lookup_assignments
        : []
      if (assignments.length) {
        const lookups = Array.isArray(item?.attachment_lookups) ? item.attachment_lookups : []
        return assignments.map(a => {
          const dept = this.departmentLabelFromAssignment(a, isArabic)
            || (a.attachment_department_code ? String(a.attachment_department_code) : '')
            || (a.attachment_department_id != null ? `#${a.attachment_department_id}` : '')
          const lookup = lookups.find(l => Number(l.id) === Number(a.attachment_lookup_id))
          let lu = ''
          if (lookup) {
            lu = this.lookupLabel(lookup, isArabic)
            const extras = this.lookupMetaSuffix(lookup)
            if (extras) lu = `${lu} (${extras})`
          } else if (a.attachment_lookup_id != null) {
            lu = `#${a.attachment_lookup_id}`
          }
          if (dept && lu) return `${dept}: ${lu}`
          return lu || dept || ''
        }).filter(Boolean)
      }

      const lookups = Array.isArray(item?.attachment_lookups) ? item.attachment_lookups : []
      if (lookups.length) {
        return lookups.map(lookup => {
          const lu = this.lookupLabel(lookup, isArabic)
          const dept = this.departmentLabelFromLookup(lookup, isArabic)
          const extras = this.lookupMetaSuffix(lookup)
          const core = dept && lu ? `${dept}: ${lu}` : lu
          return extras ? `${core} (${extras})` : core
        }).filter(Boolean)
      }

      const ids = Array.isArray(item?.attachment_lookup_ids) ? item.attachment_lookup_ids : []
      return ids.map(id => `#${id}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
